import { useState, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import Script from 'next/script'
import styled from 'styled-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { submitBobderdoo } from 'src/utils/apply/boberdoo'
import LeadIdToken from 'src/components/Apply/LeadIdToken'
import Layout from 'src/components/Layout'
import { getAllVerticals, getVertical } from 'src/utils/contentful'
import { H1, SubHeading1, TextBody } from 'src/styles/Typography'
import ProgressBar, { InlineProgressBar } from 'src/components/Apply/ProgressBar'

import grabColor from 'src/utils/grabColor'
import { StyledButton } from 'src/styles/Button'
import verticalsSteps from 'src/utils/apply/steps'
import CheckElement from 'src/components/form/Check'
import { formatPhoneNumber } from 'src/utils/apply/boberdoo'
import SvgCheck from 'src/components/icons/Check'
import FooterCTA from 'src/components/Apply/FooterCTA'
import InsurersParade from 'src/components/Apply/InsurersParade'

const queryClient = new QueryClient()

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

const validatePhone = (phone) => {
  // https://regex101.com/r/W2wtR8/1
  const re =
    /(?=(?:^(?:\+?1\s*(?:[.-]\s*)?)?(?!(?:(?:.*\(.*)|(?:.*\).*)))(?:[2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))|(?:.*\((?:[2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\).*))(?:\+?1\s*(?:[.-]\s*)?)?(?:\(?([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\)?)\s*(?:[.-]\s*)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})$/
  return re.test(phone)
}

const formatDate = ({ month, day, year }) => {
  return `${month}/${day}/${year}`
}

const calculateAge = ({ month, day, year }) => {
  if (!month || !day || !year) return false
  const dob = new Date(`${month}/${day}/${year}`)
  const now = new Date()
  const diffMs = now.getTime() - dob.getTime()
  const diffYears = diffMs / (365 * 1000 * 60 * 60 * 24)
  return diffYears
}

// Get all the names from steps
const stepNames = (currentSteps) => {
  return currentSteps
    .flatMap((step) => step.form)
    .flatMap((form) =>
      form.componentType === 'multi-input' || form.componentType === 'multi-select'
        ? form.children.map((x) => x.props.name)
        : form.name,
    )
}

// get all steps with checkboxes
const getAllCheckboxesSteps = (currentSteps) => {
  return currentSteps
    .flatMap((step) => {
      return step.form.filter((f) => f?.componentType === 'checkboxes')
    })
    .map((x) => x.name)
}

const defaultValues = {
  estimated_household_income: 65000,
  coverage: '5000',
}

const VerticalStep = ({ verticals, vertical, step, flow }) => {
  const formRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [prePopSkipList, setPrePopSkipList] = useState([])
  const verticalSlug = vertical?.slug?.replace('-insurance', '')
  const router = useRouter()

  const currentVerticalAndFlow = verticalsSteps.find((verticalStep) => {
    return verticalStep.vertical === vertical.slug && verticalStep.flow === flow
  })

  const currentSteps = currentVerticalAndFlow.steps

  const initialFormState = stepNames(currentSteps).reduce((acc, name) => {
    if (getAllCheckboxesSteps(currentSteps).includes(name)) {
      acc[name] = []
    } else if (name === 'optin') {
      acc[name] = true
    } else {
      acc[name] = defaultValues[name] || ''
    }
    return acc
  }, {})

  const [form, setForm] = useState(initialFormState)

  const handleSetForm = ({ target: { name, value } }) => {
    setForm((form) => ({ ...form, [name]: value }))
  }

  const handleSetOptin = () => {
    setForm((form) => ({ ...form, optin: !form?.optin }))
  }

  const handleSetRangeSlider = ({ name, value }) => {
    setForm((form) => ({ ...form, [name]: value }))
  }

  const handleSetSelect = ({ name, value, id }) => {
    setForm((form) => ({ ...form, [name]: id }))
  }

  const handleSetFormCheckboxes = ({ target: { name, value } }) => {
    if (!value) {
      setForm((f) => ({ ...f, [name]: [] }))
    } else if (form[name].includes(value)) {
      setForm((f) => ({
        ...f,
        [name]: f[name].filter((x) => x !== value),
      }))
    } else {
      setForm((f) => ({ ...f, [name]: [...f[name], value] }))
    }
  }

  // console.log('step: ', step)
  // console.log('flow: ', flow)
  // console.log('vertical: ', vertical.slug)
  const currentStepIndex = router.query.step
  // console.log('currentStepIndex: ', currentStepIndex)

  const currentStepLength = currentSteps.length

  // console.log('currentSteps: ', currentSteps)

  const currentStep = currentSteps[parseInt(currentStepIndex) - 1]

  // console.log('currentStep: ', currentStep)

  const { query, isReady } = router

  const queryParamList = useMemo(
    () => ({
      ...(query.SRC && { SRC: query.SRC }),
      ...(query.utm_campaign && { utm_campaign: query.utm_campaign }),
      ...(query.campaign && { utm_campaign: query.utm_campaign }),
      ...(query.Sub_ID && { Sub_ID: query.Sub_ID }),
      ...(query.Pub_ID && { Pub_ID: query.Pub_ID }),
      ...(query.first && { first_name: query.first }),
      ...(query.last && { last_name: query.last }),
      ...(query.gender && { gender: query.gender }),
      ...(query.phone && { phone_1: query.phone }),
      ...(query.address && { address_1: query.address }),
      ...(query.zip && { zip_code: query.zip }),
      ...(query.email && { email: query.email }),
      ...(query.phone && { phone: query.phone }),
      ...(query.dob && { dob: query.dob }),
      ...(query.existing && { currently_insured: query.existing }),
      ...(query.coverage && { coverage: query.coverage }),
      ...(query.conditions && { conditions: [] }),
      ...(query.transactionID && { transactionID: query.transactionID }),
      ...(query.Transaction_id && { Transaction_id: query.Transaction_id }),
      ...(query.transaction_id && { transaction_id: query.transaction_id }),
      ...(query.gid && { GID: query.gid }),
      ...(query.GID && { GID: query.GID }),
      ...(query.gclid && { GCLID: query.gclid }),
      ...(query.GCLID && { GCLID: query.GCLID }),
      ...(query.msclkid && { MSCLKID: query.msclkid }),
      ...(query.MSCLKID && { MSCLKID: query.MSCLKID }),
      ...(query.fbclid && { FBCLID: query.fbclid }),
      ...(query.FBCLID && { FBCLID: query.FBCLID }),
    }),
    [query],
  )

  const filteredQueryParams = Object.keys(queryParamList).reduce((acc, key) => {
    if (queryParamList[key].includes('{') || queryParamList[key].includes('}')) {
      return acc
    }
    return {
      ...acc,
      [key]: queryParamList[key],
    }
  }, {})

  useEffect(() => {
    // set initial form state based on query params
    if (isReady) {
      const splitAtBirth = filteredQueryParams?.dob?.split('/')
      if (currentVerticalAndFlow.autoAdvance) {
        setPrePopSkipList([
          ...Object.keys(filteredQueryParams),
          ...(splitAtBirth?.length === 3 ? ['month', 'day', 'year'] : []),
        ])
      }
      setForm((f) => ({
        ...f,
        ...filteredQueryParams,
        ...(splitAtBirth?.length === 3 && {
          month: splitAtBirth[0]?.replace(/^0+/, ''),
          day: splitAtBirth[1]?.replace(/^0+/, ''),
          year: splitAtBirth[2]?.replace(/^0+/, ''),
          age: calculateAge({
            month: splitAtBirth[0],
            day: splitAtBirth[1],
            year: splitAtBirth[2],
          }),
        }),
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, verticalSlug])

  useEffect(() => {
    setForm((f) => ({ ...initialFormState, ...f }))
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verticalSlug])

  useEffect(() => {
    // fixes bug where focus is set on last steps input
    formRef.current.focus()
    document.getElementsByTagName('main')[0].querySelectorAll('input')?.[0]?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex])

  const nextStep = `${parseInt(currentStepIndex) + 1}`
  // console.log('nextStep: ', nextStep)

  // for disabling continue button
  const currentStepInputs = currentStep.form.flatMap((form) =>
    form.componentType === 'multi-input' || form.componentType === 'multi-select'
      ? form.children.map((x) => x.props.name)
      : form.name,
  )

  const isFilledOut = currentStepInputs
    .filter((s) => s !== 'optin')
    .every((value) => form[value] && form[value] !== '')

  // console.log('isFilledOut: ', isFilledOut)

  useEffect(() => {
    // skip prepop if user has already filled out form

    if (prePopSkipList.length) {
      // if the currentStep form has been filled out, skip prepop
      const skipPrePop = currentStepInputs.every((key) => {
        return prePopSkipList.includes(key) && form[key] !== ''
      })
      if (skipPrePop) {
        // remove the currentStep form from the prePopSkipList
        setPrePopSkipList((list) => {
          return list.filter((key) => !currentStepInputs.includes(key))
        })
        // goto next step
        setLoading(false)
        router.push(
          `/apply/${vertical.slug}/${flow}/${nextStep}`,
          `/apply/${vertical.slug}/${flow}/${nextStep}`,
          { shallow: true },
        )
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, prePopSkipList])

  useEffect(() => {
    if (isReady) {
      if (window.dataLayer) {
        window.dataLayer.push({
          type: verticalSlug,
          vertical: verticalSlug,
          funnel: flow,
          src: form?.SRC || queryParamList?.SRC,
          SRC: form?.SRC || queryParamList?.SRC,
          step: currentStepIndex,
          utm_campaign: form?.utm_campaign || form?.campaign,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, currentStepIndex])

  const handleClick = (e = false) => {
    if (e) {
      e.preventDefault()
    }
    handleSetForm(e)

    // if there are multiple cards for the same step, we need to check if all the cards are filled
    if (currentStep.form.length > 1) {
      const allFilled = currentStep.form.every(
        (f) => form[f.name] || e.target.name === f.name,
      )
      // console.log('all filled: ', allFilled)
      if (!allFilled) {
        return
      }
    }
    if (
      (verticalSlug === 'auto' &&
        e.target.name === 'currently_insured' &&
        e.target.value === 'No') ||
      (verticalSlug === 'auto' &&
        e.target.name === 'tickets_accidents_claims_past_3_years' &&
        e.target.value === 'Yes')
    ) {
      gotoNextStep(false, true)
    } else {
      gotoNextStep()
    }
  }

  const gotoPrevStep = (e = false, skip = false) => {
    if (e) {
      e.preventDefault()
    }
    setPrePopSkipList([])

    if (currentStepIndex > 1) {
      const prevStep = `${parseInt(currentStepIndex) - 1}`
      router.push(
        `/apply/${vertical.slug}/${flow}/${skip ? parseInt(prevStep) - 1 : prevStep}`,
        `/apply/${vertical.slug}/${flow}/${skip ? parseInt(prevStep) - 1 : prevStep}`,
        { shallow: true },
      )
    }
  }

  const gotoNextStep = (e = false, skip = false, prevent = true) => {
    if (e && prevent) {
      e.preventDefault()
    }
    setError(null)

    const yearRegex = new RegExp('^(19|20)\\d{2}$')
    const monthRegex = new RegExp('^(0[1-9]|1[012]|[1-9])$')
    const dayRegex = new RegExp('^(0[1-9]|[12][0-9]|3[01]|[1-9])$')
    const zipRegex = new RegExp('^\\d{5}$')

    const yearValid = yearRegex.test(form.year)
    const monthValid = monthRegex.test(form.month)
    const dayValid = dayRegex.test(form.day)
    const zipValid = zipRegex.test(form.zip_code)

    if (currentStep?.stepRole === 'dob-single-step') {
      if (!yearValid || !monthValid || !dayValid) {
        setError(
          `invalid date of birth. ${!yearValid ? 'year must be 4 digits. ' : ''} ${
            !monthValid ? 'month must be 2 digits (1 - 12). ' : ''
          } ${!dayValid ? 'day must be 2 digits (1 - 31).' : ''}`,
        )
        return
      }
    }

    if (currentStep?.stepRole === 'zip_code') {
      if (!zipValid) {
        setError('zip code must be 5 digits')
        return
      }
    }

    // handle age and medicare/life redirects
    if (
      e &&
      !form.age &&
      form.day &&
      form.month &&
      (e.target.name === 'year' || form.year)
    ) {
      const year = e.target.name === 'year' ? e.target.value : form.year
      const age = calculateAge({
        month: form.month,
        day: form.day,
        year,
      })
      setForm((f) => ({ ...f, age }))
      const under64 = age < 64
      if (under64 && verticalSlug === 'medicare') {
        // redirect to health insurance and send data
        setLoading(true)
        router.push({
          pathname: `/apply/health-insurance/${flow}/4`,
          query: {
            ...form,
            dob: formatDate({ month: form.month, day: form.day, year }),
            // coverage: '10000',
            // conditions: true,
          },
        })
      }
      // if (!under64 && verticalSlug === 'life') {
      //   // redirect to medicare and send data
      //   setLoading(true)
      //   router.push({
      //     pathname: `/apply/medicare/${flow}/3`,
      //     query: {
      //       exisiting: 'Yes',
      //       ...form,
      //       dob: formatDate({ month: form.month, day: form.day, year }),
      //     },
      //   })
      // }
    }

    if (currentStepIndex < currentStepLength) {
      router.push(
        `/apply/${vertical.slug}/${flow}/${skip ? parseInt(nextStep) + 1 : nextStep}`,
        `/apply/${vertical.slug}/${flow}/${skip ? parseInt(nextStep) + 1 : nextStep}`,
        { shallow: true },
      )
    } else {
      // if (!monthValid || !dayValid || !yearValid) {
      //   // find dob step index
      //   const dobStepIndex = currentSteps.findIndex((s) => s.stepName === 'dob') + 1
      //   // redirect to dob step
      //   if (dobStepIndex) {
      //     router.push(
      //       `/apply/${vertical.slug}/${flow}/${dobStepIndex}`,
      //       `/apply/${vertical.slug}/${flow}/${dobStepIndex}`,
      //       { shallow: true },
      //     )
      //     return
      //   } else {
      //     setError('Please enter a valid date of birth.')
      //     return
      //   }
      // }

      if (!zipValid) {
        // find zip step index
        const zipStepIndex = currentSteps.findIndex((s) => s.stepRole === 'zip_code') + 1
        // redirect to zip step
        if (zipStepIndex) {
          router.push(
            `/apply/${vertical.slug}/${flow}/${zipStepIndex}`,
            `/apply/${vertical.slug}/${flow}/${zipStepIndex}`,
            { shallow: true },
          )
          setError('Please enter a 5 digit zip code.')
          return
        } else {
          setError('Please enter a 5 digit zip code.')
          return
        }
      }

      // submit form
      setLoading(true)
      setError(null)

      // validate email
      if (!validateEmail(form.email)) {
        setError('Please enter a valid email address')
        setLoading(false)
        return
      }

      // validate phone
      if (!validatePhone(form.phone_1 || form.phone || '')) {
        setError('Please enter a valid phone number')
        setLoading(false)
        return
      }

      const xxTrustedFormToken = document.getElementById('xxTrustedFormToken_0')
      const xxTrustedFormCertUrl = document.getElementById('xxTrustedFormCertUrl_0')
      const xxTrustedFormTokenValue = xxTrustedFormToken && xxTrustedFormToken?.value
      const xxTrustedFormCertUrlValue =
        xxTrustedFormCertUrl && xxTrustedFormCertUrl?.value

      const formSubmitData = {
        ...form,
        ...(xxTrustedFormTokenValue && {
          Trusted_Form_Token: xxTrustedFormTokenValue,
        }),
        ...(xxTrustedFormCertUrlValue && {
          Trusted_Form_Cert_URL: xxTrustedFormCertUrlValue,
        }),
      }

      return submitBobderdoo(formSubmitData, verticalSlug)
        .then(async (res) => {
          if (window.dataLayer) {
            window.dataLayer.push({
              type: verticalSlug,
              src: formSubmitData?.SRC,
              SRC: formSubmitData?.SRC,
              currently_insured: form?.currently_insured,
              phone: formatPhoneNumber(form.phone_1),
              zip: form?.zip_code,
              utm_campaign: formSubmitData?.utm_campaign || formSubmitData?.campaign,
              event: `${verticalSlug}-submitted`,
              funnel: flow,
            })
          }
          if (formSubmitData.optin && verticalSlug !== 'life') {
            console.log('submit life insurance')
            const lifeSrc = formSubmitData.SRC
              ? formSubmitData.SRC.includes('-Med')
                ? `${formSubmitData.SRC.split('-Med')[0]}-Life`
                : formSubmitData.SRC
              : 'CMI_PS_Organic-Life'

            if (
              currentVerticalAndFlow.homeCX &&
              formSubmitData.optin &&
              verticalSlug === 'auto' &&
              formSubmitData.homeowner === 'Yes'
            ) {
              await submitBobderdoo(
                {
                  ...formSubmitData,
                },
                'home',
              )
            } else {
              await submitBobderdoo(
                {
                  ...formSubmitData,
                  insurance_type: 'term',
                  insurance_amount: 100000,
                  term_length: '15',
                  insurance_term: '15',
                  src: lifeSrc,
                  SRC: lifeSrc,
                },
                'life',
              )
            }
          }
          if (formSubmitData.optin && verticalSlug === 'life') {
            console.log('submit health insurance')
            const healthSrc = formSubmitData.SRC
              ? formSubmitData.SRC.includes('-')
                ? `${formSubmitData.SRC.split('-')[0]}-Health`
                : formSubmitData.SRC
              : 'CMI_PS_Organic-Health'

            const medicareSrc = formSubmitData.SRC
              ? formSubmitData.SRC.includes('-')
                ? `${formSubmitData.SRC.split('-')[0]}-Med`
                : formSubmitData.SRC
              : 'CMI_PS_Organic-Med'

            if (formSubmitData.age <= 64) {
              await submitBobderdoo(
                {
                  insurance_amount: 20000,
                  term_length: '15',
                  insurance_term: '15',
                  estimated_household_income: '40000',
                  household_size: '1',
                  ...formSubmitData,
                  src: healthSrc,
                  SRC: healthSrc,
                },
                'health',
              )
            } else {
              await submitBobderdoo(
                {
                  ...formSubmitData,
                  src: medicareSrc,
                  SRC: medicareSrc,
                },
                'medicare',
              )
            }
          }
          router.push({
            pathname: `/apply/${vertical.slug}/${
              currentVerticalAndFlow?.thankYouPage || 'thank-you'
            }`,
            query: { ...form, phone_1: formatPhoneNumber(form.phone_1) },
          })
        })
        .catch((err) => {
          console.log('err: ', err)
          setLoading(false)
          setError(err)
        })
    }
  }

  const handleOnSubmit = (e) => {
    e.preventDefault()
    if (!isFilledOut && !currentStep?.allowSkip) return

    gotoNextStep()
  }

  const seo = {
    title: `Apply Now | ${vertical.title}`,
    description: `Get started by comparing quotes on ${
      vertical.title === 'Medicare' ? 'Medicare Plans' : vertical.title
    }.`,
  }

  const submitFormText =
    currentVerticalAndFlow.design === 'b' ? 'View Plans' : 'Get My Free Quotes →'

  return (
    <Layout
      verticals={verticals}
      vertical={vertical}
      seo={seo}
      header={{
        apply: true,
        disableFlyout: true,
        design: currentVerticalAndFlow.design || 'a',
        disableLink: true,
        // govAgency: verticalSlug === 'medicare' ? true : false,
      }}
      footer={{ noBorder: true, apply: true }}
    >
      <Script key={`jornayascript`} id="LeadiDscript" type="text/javascript">
        {`
                        (function() {
                        var s = document.createElement('script');
                        s.id = 'LeadiDscript_campaign';
                        s.type = 'text/javascript';
                        s.async = true;
                        s.src = '//create.lidstatic.com/campaign/f9212193-4afd-a49a-83a4-b38cd5943d4b.js?snippet_version=2';
                        var LeadiDscript = document.getElementById('LeadiDscript');
                        LeadiDscript.parentNode.insertBefore(s, LeadiDscript);
                        })();
                    `}
      </Script>
      <Head>
        <noscript
          key={`jornayanoscript`}
        >{`<img src='//create.leadid.com/noscript.gif?lac=175deec7-46ac-376d-3c9f-2e7d7b607660&lck=f9212193-4afd-a49a-83a4-b38cd5943d4b&snippet_version=2' />`}</noscript>
      </Head>
      <QueryClientProvider client={queryClient}>
        <Main design={currentVerticalAndFlow.design || 'a'}>
          {currentVerticalAndFlow.design !== 'b' && (
            <ProgressBar percent={(currentStepIndex / currentStepLength) * 100} />
          )}
          <div ref={formRef} tabIndex="-1" style={{ outline: 0 }} />
          <currentStep.Container>
            <FromWrapper
              onSubmit={handleOnSubmit}
              design={currentVerticalAndFlow.design || 'a'}
              id={
                currentStepIndex / currentStepLength === 1 ? `${verticalSlug}-submit` : ''
              }
            >
              <LoadingFieldset disabled={loading} aria-busy={loading}>
                {currentStepIndex / currentStepLength === 1 &&
                  currentVerticalAndFlow.design === 'b' && (
                    <LastQuestion>
                      <SvgCheck />
                      <span>
                        Last question! We just need to know how to get in touch with you!
                      </span>
                    </LastQuestion>
                  )}
                {currentVerticalAndFlow.design === 'b' &&
                  currentStepIndex / currentStepLength !== 1 && (
                    <InlineProgressBar
                      percent={(currentStepIndex / currentStepLength) * 100}
                      inline
                    />
                  )}
                {currentStep.TitleContainer && (
                  <currentStep.TitleContainer>
                    {currentStep?.title && (
                      <Title
                        design={currentVerticalAndFlow.design || 'a'}
                        lastStep={currentStepIndex / currentStepLength === 1}
                      >
                        {currentStep.title}
                      </Title>
                    )}

                    {currentStep?.subTitle && (
                      <SubHeading1>{currentStep.subTitle}</SubHeading1>
                    )}
                  </currentStep.TitleContainer>
                )}
                {currentStep?.form &&
                  currentStep.form.map((row, key1) => {
                    if (row.children && row?.componentType === 'multi-input') {
                      return (
                        <row.Container key={`multi-input-${key1}`}>
                          {row.children.map((child, key2) => (
                            <child.component
                              key={key2}
                              onChange={handleSetForm}
                              value={form?.[child.props.name]}
                              name={child.name}
                              {...child.props}
                            />
                          ))}
                        </row.Container>
                      )
                    }
                    if (row.children && row?.componentType === 'multi-select') {
                      return (
                        <row.Container key={`multi-select-${key1}`}>
                          {row.children.map((child, key2) => {
                            return (
                              <child.component
                                key={key2}
                                onChange={
                                  child?.props?.options ? handleSetSelect : handleSetForm
                                }
                                value={form?.[child.props.name]}
                                name={child.name}
                                {...child.props}
                              />
                            )
                          })}
                        </row.Container>
                      )
                    }
                    if (row.children) {
                      return (
                        <row.Container key={`childrensss-${key1}`}>
                          {row.children.map((child, key2) => {
                            return (
                              <child.component
                                key={key2}
                                handleClick={handleClick}
                                selected={form?.[row.name] === child.props.value}
                                name={row.name}
                                {...child.props}
                              />
                            )
                          })}
                        </row.Container>
                      )
                    }
                    if (row.component && row?.componentType === 'input') {
                      return (
                        <row.Container key={`input-${key1}`}>
                          <row.component
                            onChange={handleSetForm}
                            value={form?.[row.name]}
                            name={row.name}
                            gotoNextStep={gotoNextStep}
                            {...row.props}
                          />
                        </row.Container>
                      )
                    }
                    if (row.component && row?.componentType === 'select') {
                      return (
                        <row.Container key={`select-${key1}`}>
                          <row.component
                            onChange={handleSetSelect}
                            value={form?.[row.name]}
                            name={row.name}
                            {...row.props}
                          />
                        </row.Container>
                      )
                    }
                    if (row.component && row?.componentType === 'range-slider') {
                      return (
                        <row.Container key={`range-slider-${key1}`}>
                          <row.component
                            onChange={(value) =>
                              handleSetRangeSlider({ name: row.name, value })
                            }
                            value={form?.[row.name]}
                            name={row.name}
                            {...row.props}
                          />
                        </row.Container>
                      )
                    }
                    if (row.component && row?.componentType === 'checkboxes') {
                      return (
                        <row.Container key={`checkboxes-${key1}`}>
                          <row.component
                            {...row.props}
                            handleOnChange={handleSetFormCheckboxes}
                            value={form?.[row.name]}
                            name={row.name}
                          />
                        </row.Container>
                      )
                    }
                    if (row.component && row?.componentType === 'optin') {
                      const lifeOptinLabel = form.age <= 64 ? 'Health' : 'Medicare'

                      const autoOptinLabel =
                        currentVerticalAndFlow?.homeCX && form?.homeowner === 'Yes'
                          ? 'Home'
                          : 'Life'

                      const restProps = {
                        ...(verticalSlug === 'life' && {
                          label: `Yes, I would also like free quotes for ${lifeOptinLabel} Insurance`,
                        }),
                        ...(verticalSlug === 'auto' && {
                          label: `Yes, I would also like free quotes for ${autoOptinLabel} Insurance`,
                        }),
                      }

                      return (
                        <row.Container key={`optin-${key1}`}>
                          <CheckElement
                            {...row.props}
                            name="optin"
                            checked={form.optin === true}
                            handleOnChange={handleSetOptin}
                            {...restProps}
                          />
                        </row.Container>
                      )
                    }
                    if (row.component) {
                      const passData = {
                        ...(form?.veh_year && { veh_year: form.veh_year }),
                        ...(form?.veh_make && { veh_make: form.veh_make }),
                        ...(form?.veh_model && { veh_model: form.veh_model }),
                      }
                      return (
                        <row.Container key={`component-${key1}`}>
                          <row.component
                            handleClick={handleClick}
                            value={form?.[row.name]}
                            name={row.name}
                            passData={row.passData ? passData : null}
                            {...row.props}
                          />
                        </row.Container>
                      )
                    }
                    return null
                  })}
                <LeadIdToken />
                {error && (
                  <Error design={currentVerticalAndFlow.design || 'a'}>{error}</Error>
                )}
                {currentStep?.continue && (
                  <ButtonContainer
                    design={currentVerticalAndFlow.design || 'a'}
                    lastStep={currentStepIndex / currentStepLength === 1}
                  >
                    <StyledButton
                      // href={`/apply/${vertical.slug}/${flow}/${parseInt(currentStep) + 1}`}
                      size="large"
                      width="100%"
                      onClick={gotoNextStep}
                      type="submit"
                      disabled={!isFilledOut && !currentStep?.allowSkip}
                      data-cy="continue-button"
                    >
                      {currentStep?.allowSkip
                        ? 'Continue'
                        : currentStepIndex / currentStepLength === 1
                        ? submitFormText
                        : 'Continue'}
                    </StyledButton>
                  </ButtonContainer>
                )}
                {currentStep?.actionMeta && (
                  <ActionMetaContainer skip={currentStep?.allowSkip}>
                    <ActionMetaPlaceholder />
                    <TextBody>
                      {currentStep.actionMeta}{' '}
                      {currentStep?.allowSkip && (
                        <SkipBtn onClick={gotoNextStep} data-cy="skip-button">
                          skip
                        </SkipBtn>
                      )}
                    </TextBody>
                  </ActionMetaContainer>
                )}
                {currentStepIndex / currentStepLength === 1 && (
                  <>
                    <input type="hidden" id="leadid_tcpa_disclosure" />
                    <LegalCopy
                      className="tcpa"
                      htmlFor="leadid_tcpa_disclosure"
                      design={currentVerticalAndFlow.design || 'a'}
                    >
                      By clicking the button and submitting this form, I agree that I am
                      18+ years old and agree to the{' '}
                      <a href="/privacy" target="_blank">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href="/terms" target="_blank">
                        Terms &amp; Conditions
                      </a>
                      . By clicking the button and submitting this form, I provide my
                      signature giving express consent to receive marketing communications
                      via automated telephone dialing systems, artificial or pre-recorded
                      voices, emails, live phone calls, pre-recorded calls, postal mail,
                      text messages via SMS or MMS and other forms of communication
                      regarding offers Medicare Supplement, Medicare Advantage, Part D,
                      Final Expense and other senior home, health and assistance products
                      from PolicyScout, GuideToInsure, InsureaLife and/or one or more of
                      it&apos;s{' '}
                      <a href="/partners" target="_blank">
                        marketing partners
                      </a>{' '}
                      and agents to the number(s) and/or email I provided, including a
                      mobile phone, even if I am on a state or federal Do Not Call and/or
                      Do Not Email registry. The list of companies participating are
                      subject to change. I understand my wireless carrier may impose
                      charges for calls or texts. I understand that my consent to receive
                      communications is not a condition of purchase and I may revoke my
                      consent at any time by contacting us via 1-888-912-2132 or email at
                      opt.out@policyscout.com so that you may be placed on our do-not-call
                      and/or our do-not-email list. To receive quotes without providing
                      consent, please call at 888-912-2132.
                    </LegalCopy>
                  </>
                )}
                {currentVerticalAndFlow.showPrev &&
                  !currentStep.disablePrevBtn &&
                  currentStepIndex > 1 && (
                    <>
                      {currentStep?.allowSkip && currentVerticalAndFlow.design === 'b' ? (
                        <SkipBackWrapper>
                          <BackBtn
                            type="button"
                            onClick={gotoPrevStep}
                            design={currentVerticalAndFlow.design || 'a'}
                            data-cy="back-button"
                          >
                            ← &nbsp;Previous Question
                          </BackBtn>
                          <strong>or</strong>
                          <SkipBtnB onClick={gotoNextStep} data-cy="skip-button">
                            skip &nbsp;→
                          </SkipBtnB>
                        </SkipBackWrapper>
                      ) : (
                        <BackBtn
                          type="button"
                          onClick={gotoPrevStep}
                          design={currentVerticalAndFlow.design || 'a'}
                          data-cy="back-button"
                        >
                          ← &nbsp;Previous Question
                        </BackBtn>
                      )}
                    </>
                  )}
              </LoadingFieldset>
            </FromWrapper>
          </currentStep.Container>
        </Main>
      </QueryClientProvider>
      {currentVerticalAndFlow.design === 'b' && <FooterCTA verticalSlug={verticalSlug} />}
      {currentVerticalAndFlow.design === 'b' && (
        <InsurersParade verticalSlug={verticalSlug} />
      )}
    </Layout>
  )
}

const LastQuestion = styled.div`
  display: flex;
  align-items: center;
  background: ${grabColor('success.border')};
  border-radius: 2px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  padding: 1rem;
  width: calc(100% + 1rem);
  margin-left: -0.5rem;

  svg {
    width: 18px;
    height: 18px;
    vertical-align: middle;
    margin-top: -5px;
    margin-right: 0.6rem;
    min-width: 18px;
  }
`

const SkipBackWrapper = styled.div`
  text-align: center;
  span {
    display: inline-block;
    margin: 0 1rem;
  }
`

const SkipBtnB = styled.button`
  background: none;
  border: none;
  color: ${grabColor('base')};
  cursor: pointer;
  font-size: 14px;
  margin: 3rem auto 1rem;
  outline: 0;
  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    margin: 1.5rem auto 0;
  }
`

const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${grabColor('base')};
  cursor: pointer;
  font-size: 14px;
  margin: 3rem auto 1rem;
  outline: 0;

  ${({ design }) =>
    design === 'b' &&
    `
    margin: 40px auto 0;
  `}

  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    ${({ design }) =>
      design === 'b' &&
      `
    margin: 24px auto 0;
  `}
  }
`

const LegalCopy = styled.label`
  font-size: 12px;
  margin-top: 1rem;
  color: ${grabColor('detail')};
  line-height: 1.5;

  // b design
  ${({ design }) =>
    design === 'b' &&
    `
    margin-top: 2rem;
    `}

  a {
    color: ${grabColor('base')};

    &:hover {
      color: ${grabColor('detail')};
    }
  }
`

const Error = styled.div`
  color: ${grabColor('error.DEFAULT')};
  border: 1px solid ${grabColor('error.border')};
  background: ${grabColor('error.bg')};
  padding: 0.5rem;
  margin-top: 1rem;
  margin-bottom: -1rem;

  ${({ design }) =>
    design === 'b' &&
    `
    margin: 1rem 0;
    `}
`

const SkipBtn = styled.button`
  // reset button styles
  appearance: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-decoration: underline;

  &:hover {
    color: black;
  }
`

const ActionMetaPlaceholder = styled.div`
  margin: 0 1rem 0 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
`

const ActionMetaContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 2px auto;
  ${TextBody} {
    font-size: 12px;
  }
  ${({ skip }) =>
    skip &&
    `
    margin-top: 2rem;
  `}
`

const ButtonContainer = styled.div`
  display: flex;
  margin: 2rem 0;

  ${({ design }) =>
    design === 'b' &&
    `
    margin: 1rem auto 0;
    max-width: 160px;
    width: 100%;
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin: 1rem 0;

    ${({ design }) =>
      design === 'b' &&
      `
    margin: 1rem auto 0;
    max-width: 160px;
    width: 100%;
  `}

    ${({ lastStep, design }) => design === 'b' && lastStep && `max-width: initial;`}
  }
`

export default VerticalStep

export async function getStaticPaths() {
  // const verticals = await getAllVerticals()

  const postParams = verticalsSteps.flatMap((vertical) => {
    return vertical.steps.map((step) => ({
      params: { vertical: vertical.vertical, flow: vertical.flow, step: step.step },
    }))
  })

  return {
    paths: postParams,
    fallback: false,
  }
}

export async function getStaticProps({ params }) {
  const vertical = await getVertical(params.vertical)
  const verticals = await getAllVerticals()
  const step = params.step
  const flow = params.flow

  return { props: { verticals, vertical, step, flow } }
}

const Main = styled.main`
  position: relative;
  background: ${grabColor('backgroundLight')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 500px;

  // set background for design b
  ${({ design }) =>
    design === 'b' &&
    `
    background-image: url('/images/ocean.jpg');
    background-size: cover;
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    min-height: 400px;
  }
`

const LoadingFieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  border: 0;
  padding: 0;
  margin: 0;

  &[disabled] {
    opacity: 0.5;
  }
`

const FromWrapper = styled.form`
  margin: 2rem auto 4rem;

  ${({ design }) =>
    design === 'b' &&
    `
    background: #FAFAFB;
    box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06);
border-radius: 4px;
padding: 40px;
  margin: 80px auto;

  `}

  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    margin: 1.5rem auto 3rem;

    ${({ design }) =>
      design === 'b' &&
      `
    padding: 32px 24px;
    margin: 32px auto 40px;

  `}
  }
`

const Title = styled(H1)`
  ${({ design }) =>
    design === 'b' &&
    `
    font-size: 24px;
    text-align: center;
    line-height: 36px;
    `}
  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    font-size: 30px;
    margin: 1rem 0;

    ${({ design }) =>
      design === 'b' &&
      `
    font-size: 24px;
    text-align: center;
    `}

    ${({ lastStep, design }) => design === 'b' && lastStep && `font-size: 22px;`}
  }
`
